import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import { api } from "../../lib/api";
import { Plus, Sparkles, ShoppingBag, Activity } from "lucide-react";
import OrderStatusPill from "../../components/OrderStatusPill";
import { useAuth } from "../../lib/auth";

export default function DashboardHome() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = orders.reduce((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});
  const total = orders.length;
  const inProgress = (counts.in_progress || 0) + (counts.in_review || 0) + (counts.pending || 0);
  const delivered = counts.delivered || 0;

  return (
    <DashboardLayout>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Overview</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Hello {user?.name?.split(" ")[0]}.</h1>
          <p className="text-inkSoft mt-1">Here's a snapshot of your campaigns.</p>
        </div>
        <Link to="/dashboard/services" className="btn-primary text-sm" data-testid="dashboard-new-order">
          <Plus size={16} /> New order
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <Stat icon={ShoppingBag} label="Total orders" value={total} />
        <Stat icon={Activity} label="Active" value={inProgress} />
        <Stat icon={Sparkles} label="Delivered" value={delivered} />
      </div>

      <div className="mt-10 card-soft">
        <div className="px-6 py-5 border-b border-line flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold tracking-tight">Recent orders</h2>
          <Link to="/dashboard/orders" className="text-sm text-terra hover:underline">View all</Link>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-sm text-inkSoft">Loading…</div>
        ) : orders.length === 0 ? (
          <div className="px-6 py-10 text-sm text-inkSoft">No orders yet. <Link to="/dashboard/new-order" className="text-terra">Place your first order</Link>.</div>
        ) : (
          <ul>
            {orders.slice(0, 5).map((o) => (
              <li key={o.order_id} className="px-6 py-4 border-b border-line last:border-0 flex items-center justify-between gap-4" data-testid={`recent-order-${o.order_id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.package_name || o.service_slug}</div>
                  <div className="text-xs text-inkSoft font-mono">{o.order_id}</div>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <div className="hidden sm:block w-32 h-1.5 rounded-full bg-surfaceAlt overflow-hidden">
                    <div className="h-full bg-terra" style={{ width: `${o.progress || 0}%` }} />
                  </div>
                  <OrderStatusPill status={o.status} />
                  <Link to={`/dashboard/orders/${o.order_id}`} className="text-sm text-ink hover:text-terra">Open</Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </DashboardLayout>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-surfaceAlt grid place-items-center text-terra"><Icon size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-inkSoft">{label}</div>
          <div className="font-mono text-2xl font-semibold tracking-tighter">{value}</div>
        </div>
      </div>
    </div>
  );
}
